import { useState, useEffect } from 'react';
import { authFetch } from '@/utils/authFetch';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, TrendingUp, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Analytics() {
  const navigate = useNavigate();
  const [workload, setWorkload] = useState([]);
  const [projectHealth, setProjectHealth] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [workloadRes, healthRes, userRes] = await Promise.all([
          authFetch(`${BACKEND_URL}/api/analytics/workload`),
          authFetch(`${BACKEND_URL}/api/analytics/project-health`),
          authFetch(`${BACKEND_URL}/api/auth/me`)
        ]);

        if (userRes.ok) {
          const userData = await userRes.json();
          setCurrentUser(userData);
          if (userData.role !== 'admin') {
            toast.error('Admin access required');
            navigate('/dashboard');
            return;
          }
        }

        if (workloadRes.ok) setWorkload(await workloadRes.json());
        if (healthRes.ok) setProjectHealth(await healthRes.json());
      } catch (error) {
        toast.error('Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  const getWorkloadColor = (percentage) => {
    if (percentage >= 80) return 'bg-red-500';
    if (percentage >= 50) return 'bg-amber-500';
    return 'bg-emerald-500';
  };

  const getHealthBadge = (health) => {
    const styles = {
      healthy: 'bg-emerald-50 text-emerald-700 border-emerald-100',
      attention_needed: 'bg-amber-50 text-amber-700 border-amber-100',
      at_risk: 'bg-red-50 text-red-700 border-red-100'
    };
    return styles[health] || styles.healthy;
  };

  const getHealthIcon = (health) => {
    if (health === 'healthy') return '🟢';
    if (health === 'attention_needed') return '🟡';
    return '🔴';
  };

  if (loading) return <Layout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div></Layout>;

  return (
    <Layout>
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Analytics Dashboard</h1>
          <p className="text-slate-600 mt-1">Team workload and project health monitoring</p>
        </div>

        {/* Team Workload Section */}
        <div>
          <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center">
            <Users className="w-5 h-5 mr-2 text-indigo-600" />
            Team Workload Distribution
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {workload.map((member) => (
              <Card key={member.user_id} className="card-hover">
                <CardHeader>
                  <CardTitle className="text-base">{member.user_name}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-600">Workload</span>
                      <span className="font-semibold text-slate-900">{member.workload_percentage}%</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-3">
                      <div 
                        className={`h-3 rounded-full transition-all ${getWorkloadColor(member.workload_percentage)}`}
                        style={{ width: `${member.workload_percentage}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="bg-slate-50 p-2 rounded">
                      <p className="text-slate-600 text-xs">Total</p>
                      <p className="font-semibold text-slate-900">{member.total_tasks}</p>
                    </div>
                    <div className="bg-amber-50 p-2 rounded">
                      <p className="text-amber-600 text-xs">Pending</p>
                      <p className="font-semibold text-amber-700">{member.pending_tasks}</p>
                    </div>
                    <div className="bg-blue-50 p-2 rounded">
                      <p className="text-blue-600 text-xs">In Progress</p>
                      <p className="font-semibold text-blue-700">{member.in_progress_tasks}</p>
                    </div>
                    <div className="bg-emerald-50 p-2 rounded">
                      <p className="text-emerald-600 text-xs">Completed</p>
                      <p className="font-semibold text-emerald-700">{member.completed_tasks}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Project Health Section */}
        <div>
          <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center">
            <TrendingUp className="w-5 h-5 mr-2 text-indigo-600" />
            Project Health Monitor
          </h2>
          <div className="grid grid-cols-1 gap-4">
            {projectHealth.map((project) => (
              <Card key={project.project_id} className="card-hover">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{getHealthIcon(project.health)}</span>
                        <div>
                          <CardTitle className="text-base">{project.project_name}</CardTitle>
                          <p className="text-sm text-slate-500 mt-1">Status: {project.status}</p>
                        </div>
                      </div>
                    </div>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getHealthBadge(project.health)}`}>
                      {project.health.replace('_', ' ')}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-slate-600">Progress</span>
                        <span className="font-semibold text-slate-900">{project.progress}%</span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2">
                        <div 
                          className="h-2 rounded-full bg-indigo-600 transition-all"
                          style={{ width: `${project.progress}%` }}
                        ></div>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                      <div className="text-center">
                        <p className="text-xs text-slate-600">Total Tasks</p>
                        <p className="text-lg font-bold text-slate-900">{project.total_tasks}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-slate-600">Completed</p>
                        <p className="text-lg font-bold text-emerald-600">{project.completed_tasks}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-slate-600">Overdue</p>
                        <p className="text-lg font-bold text-red-600">{project.overdue_tasks}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-slate-600">End Date</p>
                        <p className="text-sm font-semibold text-slate-700">{project.end_date || 'N/A'}</p>
                      </div>
                    </div>
                    {project.overdue_tasks > 0 && (
                      <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-md">
                        <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
                        <p className="text-sm text-red-700">
                          This project has {project.overdue_tasks} overdue task{project.overdue_tasks > 1 ? 's' : ''}. Immediate attention required.
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}